/**
 * Privacy levels shared by the profile-view setting and the per-field settings.
 *
 * The backend stores an integer; the UI works with the labels declared in
 * `types/profile.ts` (`ProfileVisibility` / `WeightVisibility`). Both scales use
 * the same IDs — only the label for level 3 differs ("Private" for the whole
 * profile, "Only me" for a single field).
 */
import type { ProfileVisibility, WeightVisibility } from '../types/profile';

export const PRIVACY_LEVEL = {
  PUBLIC: 1,
  BUDDIES: 2,
  PRIVATE: 3,
} as const;

export type PrivacyLevel = (typeof PRIVACY_LEVEL)[keyof typeof PRIVACY_LEVEL];

export const PRIVACY_ID_TO_PROFILE_VISIBILITY: Record<number, ProfileVisibility> = {
  [PRIVACY_LEVEL.PUBLIC]: 'Public',
  [PRIVACY_LEVEL.BUDDIES]: 'Buddies only',
  [PRIVACY_LEVEL.PRIVATE]: 'Private',
};

export const PRIVACY_ID_TO_FIELD_VISIBILITY: Record<number, WeightVisibility> = {
  [PRIVACY_LEVEL.PUBLIC]: 'Public',
  [PRIVACY_LEVEL.BUDDIES]: 'Buddies only',
  [PRIVACY_LEVEL.PRIVATE]: 'Only me',
};

/**
 * Every label either scale can produce, lower-cased, mapped back to its ID.
 * "Only Me" / "Only me" / "Private" all resolve to level 3.
 */
export const PRIVACY_LABEL_TO_ID: Record<string, PrivacyLevel> = {
  public: PRIVACY_LEVEL.PUBLIC,
  'buddies only': PRIVACY_LEVEL.BUDDIES,
  buddies: PRIVACY_LEVEL.BUDDIES,
  'only me': PRIVACY_LEVEL.PRIVATE,
  private: PRIVACY_LEVEL.PRIVATE,
};

export const PROFILE_VISIBILITY_TO_ID: Record<ProfileVisibility, PrivacyLevel> = {
  Public: PRIVACY_LEVEL.PUBLIC,
  'Buddies only': PRIVACY_LEVEL.BUDDIES,
  Private: PRIVACY_LEVEL.PRIVATE,
};

export const FIELD_VISIBILITY_TO_ID: Record<WeightVisibility, PrivacyLevel> = {
  Public: PRIVACY_LEVEL.PUBLIC,
  'Buddies only': PRIVACY_LEVEL.BUDDIES,
  'Only me': PRIVACY_LEVEL.PRIVATE,
};

/** Resolves any stored label to its numeric level, or undefined when unknown. */
export const toPrivacyLevel = (
  value: string | number | null | undefined,
): PrivacyLevel | undefined => {
  if (value == null || value === '') return undefined;
  if (typeof value === 'number') {
    return PRIVACY_ID_TO_PROFILE_VISIBILITY[value] ? (value as PrivacyLevel) : undefined;
  }
  return PRIVACY_LABEL_TO_ID[value.trim().toLowerCase()];
};
